import { useEffect, useState, useCallback } from "react";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { INCOME_CATEGORIES, EXPENSE_CATEGORIES, LIVESTOCK } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Trash2, Printer, ArrowUpRight, ArrowDownRight, CheckCircle2, Repeat, RefreshCw, Power } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { exportPdf } from "../lib/pdf";

const txSection = (txs, type, heading) => {
  const list = txs.filter((t) => t.type === type);
  return {
    heading,
    columns: ["Date", "Category", "Details", type === "income" ? "Buyer / Source" : "Supplier", "Status", "Amount"],
    rows: list.map((t) => [t.date, t.category, t.description, t.party, t.status, { money: t.amount }]),
    foot: ["", "", "", "", "Total", { money: list.reduce((s, t) => s + t.amount, 0) }],
  };
};

const recIncomeBlank = () => ({ category: "", description: "", amount: 0, party: "", livestock_type: "general", day_of_month: 1, active: true, flow: "income" });

const emptyTx = (type) => ({
  type,
  category: "",
  description: "",
  amount: 0,
  status: type === "income" ? "received" : "paid",
  party: "",
  livestock_type: "general",
  date: new Date().toISOString().slice(0, 10),
});

function TxDialog({ type, onSaved }) {
  const { settings } = useFarm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTx(type));
  const cats = type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const statuses = type === "income" ? ["received", "expected"] : ["paid", "upcoming"];

  const submit = async () => {
    if (!form.category || !form.amount) return toast.error("Category and amount are required");
    await api.createTransaction(form);
    toast.success(`${type === "income" ? "Income" : "Expense"} added`);
    setOpen(false);
    setForm(emptyTx(type));
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-10 rounded-full gap-1.5" data-testid={`add-${type}-button`}>
          <Plus className="h-4 w-4" /> Add {type === "income" ? "Income" : "Expense"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display capitalize">Add {type}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5" data-testid={`${type}-category-select`}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1.5" data-testid={`${type}-status-select`}><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount ({settings.currency_symbol})</Label>
            <Input type="number" className="mt-1.5" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} data-testid={`${type}-amount-input`} />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid={`${type}-date-input`} />
          </div>
          <div>
            <Label>Livestock</Label>
            <Select value={form.livestock_type} onValueChange={(v) => setForm({ ...form, livestock_type: v })}>
              <SelectTrigger className="mt-1.5" data-testid={`${type}-livestock-select`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                {LIVESTOCK.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{type === "income" ? "Buyer" : "Supplier"}</Label>
            <Input className="mt-1.5" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} data-testid={`${type}-party-input`} />
          </div>
          <div className="col-span-2">
            <Label>Description</Label>
            <Input className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`${type}-description-input`} />
          </div>
        </div>
        <DialogFooter><Button className="rounded-full" onClick={submit} data-testid={`save-${type}-button`}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TxTable({ type, items, onDelete, onConvert }) {
  const { fmt } = useFarm();
  const list = items.filter((t) => t.type === type);
  if (!list.length) return <p className="py-12 text-center text-sm text-muted-foreground">No {type} recorded.</p>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid={`${type}-table`}>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>{type === "income" ? "Buyer" : "Supplier"}</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((t) => (
            <TableRow key={t.id} data-testid={`${type}-row-${t.id}`}>
              <TableCell className="font-mono-nums text-sm">{t.date}</TableCell>
              <TableCell className="font-medium">{t.category}{t.description && <span className="block text-xs text-muted-foreground">{t.description}</span>}</TableCell>
              <TableCell className="text-sm">{t.party || "—"}</TableCell>
              <TableCell>
                <Badge variant={["received", "paid"].includes(t.status) ? "default" : "secondary"} className="capitalize">{t.status}</Badge>
              </TableCell>
              <TableCell className="text-right font-mono-nums font-semibold">{fmt(t.amount)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {type === "income" && t.status === "expected" && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--emerald))]" onClick={() => onConvert(t)} data-testid={`convert-income-${t.id}`} title="Mark as received">
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(t.id)} data-testid={`delete-${type}-${t.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Financials() {
  const { fmt, settings } = useFarm();
  const [txs, setTxs] = useState([]);
  const [pnl, setPnl] = useState(null);

  const load = useCallback(() => {
    api.getTransactions().then(setTxs);
    api.pnl().then(setPnl);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => { await api.deleteTransaction(id); load(); toast.success("Deleted"); };

  const payloadOf = (t, status) => ({ type: t.type, category: t.category, description: t.description, amount: t.amount, status, party: t.party, livestock_type: t.livestock_type, date: t.date });
  const convert = async (t) => {
    await api.updateTransaction(t.id, payloadOf(t, "received"));
    load();
    toast.success("Marked as received");
  };
  const markAllReceived = async () => {
    const expected = txs.filter((t) => t.type === "income" && t.status === "expected");
    if (!expected.length) return toast.info("No expected income to convert");
    await Promise.all(expected.map((t) => api.updateTransaction(t.id, payloadOf(t, "received"))));
    load();
    toast.success(`Marked ${expected.length} received`);
  };

  const downloadPdf = () => {
    const sections = [];
    if (pnl) sections.push({ heading: "Summary", columns: ["Item", "Amount"], rows: [["Total Income", { money: pnl.total_income }], ["Total Expenses", { money: pnl.total_expense }], ["Net Profit", { money: pnl.net_profit }]] });
    sections.push(txSection(txs, "income", "Income (received & expected)"), txSection(txs, "expense", "Expenses & Bills"));
    exportPdf({ settings, title: "Financial Report", sections, filename: "financial-report.pdf" });
  };

  const [recIncome, setRecIncome] = useState([]);
  const [recOpen, setRecOpen] = useState(false);
  const [recForm, setRecForm] = useState(recIncomeBlank());
  const loadRecIncome = useCallback(() => api.getRecurring("income").then(setRecIncome), []);
  useEffect(() => { loadRecIncome(); }, [loadRecIncome]);
  const addRecIncome = async () => {
    if (!recForm.category || !recForm.amount) return toast.error("Category and amount required");
    await api.createRecurring(recForm);
    toast.success("Recurring income added");
    setRecOpen(false); setRecForm(recIncomeBlank()); loadRecIncome();
  };
  const toggleRecIncome = async (r) => { await api.updateRecurring(r.id, { ...r, active: !r.active }); loadRecIncome(); };
  const delRecIncome = async (id) => { await api.deleteRecurring(id); loadRecIncome(); toast.success("Removed"); };
  const runDueIncome = async () => {
    const res = await api.runRecurring();
    toast.success(res.posted ? `Posted ${res.posted} recurring item(s)` : "Nothing due this month");
    load(); loadRecIncome();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Profit &amp; Loss</h1>
          <p className="text-sm text-muted-foreground mt-1">Income, expenses and net profit statement.</p>
        </div>
        <Button variant="outline" className="h-10 rounded-full gap-1.5" onClick={downloadPdf} data-testid="export-pdf-button">
          <Printer className="h-4 w-4" /> Export PDF
        </Button>
      </div>

      {pnl && (
        <Card className="p-6 print-area" data-testid="profit-loss-table">
          <h2 className="font-display text-xl font-bold tracking-tight mb-4">P&amp;L Statement</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--emerald))] mb-2"><ArrowUpRight className="h-4 w-4" /> Income</p>
              <table className="w-full text-sm">
                <tbody>
                  {pnl.income.length ? pnl.income.map((r) => (
                    <tr key={r.category} className="border-b border-border/60">
                      <td className="py-2">{r.category}</td>
                      <td className="py-2 text-right font-mono-nums">{fmt(r.amount)}</td>
                    </tr>
                  )) : <tr><td className="py-2 text-muted-foreground">No income</td></tr>}
                  <tr className="font-bold"><td className="py-2">Total Income</td><td className="py-2 text-right font-mono-nums">{fmt(pnl.total_income)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--terracotta))] mb-2"><ArrowDownRight className="h-4 w-4" /> Expenses</p>
              <table className="w-full text-sm">
                <tbody>
                  {pnl.expenses.length ? pnl.expenses.map((r) => (
                    <tr key={r.category} className="border-b border-border/60">
                      <td className="py-2">{r.category}</td>
                      <td className="py-2 text-right font-mono-nums">{fmt(r.amount)}</td>
                    </tr>
                  )) : <tr><td className="py-2 text-muted-foreground">No expenses</td></tr>}
                  <tr className="font-bold"><td className="py-2">Total Expenses</td><td className="py-2 text-right font-mono-nums">{fmt(pnl.total_expense)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between rounded-xl bg-primary text-primary-foreground px-6 py-4">
            <span className="font-display text-lg font-bold">Net Profit</span>
            <span className="font-mono-nums text-2xl font-extrabold" data-testid="pnl-net-profit">{fmt(pnl.net_profit)}</span>
          </div>
        </Card>
      )}

      <Tabs defaultValue="income" className="no-print">
        <TabsList>
          <TabsTrigger value="income" data-testid="tab-income">Income</TabsTrigger>
          <TabsTrigger value="expense" data-testid="tab-expense">Expenses</TabsTrigger>
        </TabsList>
        <TabsContent value="income" className="mt-4 space-y-4">
          <div className="flex flex-wrap justify-end gap-2 mb-3">
            <Button variant="outline" className="h-10 rounded-full gap-1.5" onClick={markAllReceived} data-testid="mark-all-received-button"><CheckCircle2 className="h-4 w-4" /> Mark Expected Received</Button>
            <TxDialog type="income" onSaved={load} />
          </div>
          <Card><TxTable type="income" items={txs} onDelete={remove} onConvert={convert} /></Card>

          <Card className="p-6" data-testid="recurring-income-section">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div className="flex items-center gap-2">
                <Repeat className="h-5 w-5 text-[hsl(var(--emerald))]" />
                <div>
                  <h2 className="font-display text-xl font-bold tracking-tight">Recurring Income</h2>
                  <p className="text-xs text-muted-foreground">Contracts like milk deliveries — auto-posted as received on the 1st each month.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={runDueIncome} data-testid="run-recurring-income-button"><RefreshCw className="h-3.5 w-3.5" /> Run Due Now</Button>
                <Button size="sm" className="rounded-full gap-1.5" onClick={() => { setRecForm(recIncomeBlank()); setRecOpen(true); }} data-testid="add-recurring-income-button"><Plus className="h-3.5 w-3.5" /> Add Recurring</Button>
              </div>
            </div>
            {recIncome.length ? (
              <div className="space-y-2">
                {recIncome.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3.5" data-testid={`recurring-income-row-${r.id}`}>
                    <div className="flex-1 min-w-[160px]">
                      <p className="font-semibold">{r.category} <span className="font-mono-nums text-muted-foreground">· day {r.day_of_month}</span></p>
                      <p className="text-xs text-muted-foreground">{r.party || "—"}{r.description ? ` · ${r.description}` : ""}</p>
                    </div>
                    <span className="font-mono-nums font-bold text-[hsl(var(--emerald))]">{fmt(r.amount)}</span>
                    <div className="flex items-center gap-1.5">
                      <Power className={`h-3.5 w-3.5 ${r.active ? "text-[hsl(var(--emerald))]" : "text-muted-foreground"}`} />
                      <Switch checked={r.active} onCheckedChange={() => toggleRecIncome(r)} data-testid={`toggle-recurring-income-${r.id}`} />
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delRecIncome(r.id)} data-testid={`delete-recurring-income-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">No recurring income set up yet.</p>
            )}
          </Card>

          <Dialog open={recOpen} onOpenChange={setRecOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display">Add Recurring Income</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2">
                  <Label>Category</Label>
                  <Select value={recForm.category} onValueChange={(v) => setRecForm({ ...recForm, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="recurring-income-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{INCOME_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount ({settings.currency_symbol})</Label>
                  <Input type="number" className="mt-1.5" value={recForm.amount} onChange={(e) => setRecForm({ ...recForm, amount: parseFloat(e.target.value) || 0 })} data-testid="recurring-income-amount-input" />
                </div>
                <div>
                  <Label>Day of Month</Label>
                  <Input type="number" min="1" max="28" className="mt-1.5" value={recForm.day_of_month} onChange={(e) => setRecForm({ ...recForm, day_of_month: parseInt(e.target.value) || 1 })} data-testid="recurring-income-day-input" />
                </div>
                <div className="col-span-2">
                  <Label>Buyer</Label>
                  <Input className="mt-1.5" value={recForm.party} onChange={(e) => setRecForm({ ...recForm, party: e.target.value })} data-testid="recurring-income-party-input" />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Input className="mt-1.5" value={recForm.description} onChange={(e) => setRecForm({ ...recForm, description: e.target.value })} data-testid="recurring-income-description-input" />
                </div>
              </div>
              <DialogFooter><Button className="rounded-full" onClick={addRecIncome} data-testid="save-recurring-income-button">Add Recurring</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        <TabsContent value="expense" className="mt-4">
          <div className="flex justify-end mb-3"><TxDialog type="expense" onSaved={load} /></div>
          <Card><TxTable type="expense" items={txs} onDelete={remove} /></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
